/**
 * LRU cache with a byte budget. Evicts least-recently-used entries once the total
 * byte size exceeds maxBytes. Byte sizes are supplied asynchronously via updateBytes()
 * because they may only be known after an async fetch resolves.
 * On eviction, calls the optional onEvict callback (e.g. to revoke blob URLs).
 */
export class LruCache<K, V> {
    private readonly map = new Map<K, V>();
    private readonly entryBytes = new Map<K, number>();
    private _totalBytes = 0;

    constructor(
        private readonly maxBytes: number,
        private readonly onEvict?: (key: K, value: V, bytes: number) => void,
    ) {}

    get size(): number {
        return this.map.size;
    }

    get totalBytes(): number {
        return this._totalBytes;
    }

    get(key: K): V | undefined {
        if (!this.map.has(key)) return undefined;
        // Re-insert to mark as most recently used
        const value = this.map.get(key)!;
        const b = this.entryBytes.get(key)!;
        this.map.delete(key);
        this.entryBytes.delete(key);
        this.map.set(key, value);
        this.entryBytes.set(key, b);
        return value;
    }

    set(key: K, value: V): void {
        if (this.map.has(key)) {
            this._totalBytes -= this.entryBytes.get(key)!;
            this.map.delete(key);
            this.entryBytes.delete(key);
        }
        this.map.set(key, value);
        this.entryBytes.set(key, 0);
    }

    /** Call once the true byte size of an entry is known (e.g. after an async fetch resolves). */
    updateBytes(key: K, bytes: number): void {
        if (!this.map.has(key)) return;
        this._totalBytes -= this.entryBytes.get(key)!;
        this.entryBytes.set(key, bytes);
        this._totalBytes += bytes;
        this.evictIfNeeded();
    }

    private evictIfNeeded(): void {
        for (const [key, value] of this.map) {
            if (this._totalBytes <= this.maxBytes) break;
            const bytes = this.entryBytes.get(key)!;
            if (bytes === 0) continue; // skip pending entries whose size isn't known yet
            this.map.delete(key);
            this.entryBytes.delete(key);
            this._totalBytes -= bytes;
            this.onEvict?.(key, value, bytes);
        }
    }
}
